import Analytics from '@/utils/analytics';
import { ALLOWED_PACK_UNITS_FOR_CONVERSION } from '@/utils/menu';
import { DEFAULT_CURRENCY_CODE } from '@/utils/money';
import { ItemListOption } from '@/contexts/item-context-types';
import {
  ItemsContext_VariantsPackFieldsFragment,
  MenuItemSearch_IngredientFieldsFragment,
  MenuItemSearch_ManualIngredientsQuery,
  MenuItemSearch_ManualIngredientsQueryVariables,
} from '@/types';
import { ListOption } from '@/components/list-item';
import { MANUAL_MENU_ITEM_INGREDIENT_SEARCH_QUERY } from '@/components/menu/search/menu-item-search-queries';
import { ManualIngredientMetadata } from '@/components/menu/search/menu-item-search-types';
import { transformFragmentIntoManualIngredient } from '@/components/menu/search/menu-item-search-helpers';
import { useAdaptAndGroupVariantPacks } from './use-adapt-and-group-item-list-options';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useItemQueryVariables } from './use-item-query-variables';
import { useLazyQuery } from '@apollo/client';
import { useLoadAllMenuItemIngredients } from './use-load-all-menu-item-ingredients';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useSearchCatalog } from './use-search-catalog';
import { useToasts } from '@/contexts/toast-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

interface AllIngredientAccumulator {
  catalogVariantPacks: ItemsContext_VariantsPackFieldsFragment[];
  manualFragments: MenuItemSearch_IngredientFieldsFragment[];
  seenUuids: Set<string>;
}

export const useSearchMenuItemIngredients = () => {
  const itemQueryVariables = useItemQueryVariables();
  const { showErrorToast } = useToasts();
  const { currencyCode } = useVariantPackInfoContext();
  const { restaurantUUID, supplierUUID } = useNewOrderContext();
  const [queryLoading, setQueryLoading] = useState(false);
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();
  const queryRef = useRef('');

  const [manualIngredientSearchResults, setManualIngredientSearchResults] =
    useState<ListOption<ManualIngredientMetadata>[] | null>(null);

  const [searchManualMenuItemIngredients] = useLazyQuery<
    MenuItemSearch_ManualIngredientsQuery,
    MenuItemSearch_ManualIngredientsQueryVariables
  >(MANUAL_MENU_ITEM_INGREDIENT_SEARCH_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const { allIngredientsLoading, allIngredientsResponse } =
    useLoadAllMenuItemIngredients();

  const searchCatalogIngredients = useSearchCatalog({
    searchSource: 'menu-item-catalog-ingredients',
  });

  const searchManualIngredients = useCallback(
    async (query: string) => {
      if (!restaurantUUID || !supplierUUID) {
        return;
      }

      queryRef.current = query;

      if (!queryRef.current) {
        return setManualIngredientSearchResults(null);
      }

      setQueryLoading(true);
      Analytics.track('menu-item-manual-ingredients.search', {
        query,
        restaurantUUID,
        supplierUUID,
      });
      try {
        // Apollo doesn't provide a mechanism to cancel an in-flight query so
        // this ensures that results from the latest query are displayed
        const searchResults = await searchManualMenuItemIngredients({
          variables: {
            ...itemQueryVariables,
            query,
            restaurantUUID: restaurantUUID,
            supplierUUID: supplierUUID,
          },
        });
        if (query === queryRef.current) {
          const queriedIngredientsData = searchResults.data;
          if (queriedIngredientsData) {
            const manualIngredientResults =
              queriedIngredientsData?.searchManualMenuItemIngredients.reduce<
                ListOption<ManualIngredientMetadata>[]
              >((acc, { menu_item_ingredient }) => {
                if (menu_item_ingredient) {
                  acc.push(
                    transformFragmentIntoManualIngredient(
                      menu_item_ingredient,
                      currencyCode || DEFAULT_CURRENCY_CODE,
                    ),
                  );
                }
                return acc;
              }, []) ?? null;

            setManualIngredientSearchResults(manualIngredientResults);
          }
        }
      } catch (e) {
        showErrorToast();
      } finally {
        setQueryLoading(false);
      }
    },
    [
      currencyCode,
      itemQueryVariables,
      restaurantUUID,
      searchManualMenuItemIngredients,
      showErrorToast,
      supplierUUID,
    ],
  );
  const allIngredients: {
    catalog: ItemListOption[];
    manual: ListOption<ManualIngredientMetadata>[];
  } | null = useMemo(() => {
    // Create an object that contains all ingredients separated by catalog variant packs and manual ingredient fragments
    const allIngredientFragmentsLists =
      allIngredientsResponse?.menu_item_ingredients?.reduce<AllIngredientAccumulator>(
        (acc, item: MenuItemSearch_IngredientFieldsFragment) => {
          const variantPack = item.variant_pack;
          const uuid = variantPack?.uuid || item.uuid;
          if (!acc.seenUuids.has(uuid)) {
            acc.seenUuids.add(uuid);
            if (variantPack) {
              acc.catalogVariantPacks.push(variantPack);
            } else {
              acc.manualFragments.push(item);
            }
          }
          return acc;
        },
        {
          catalogVariantPacks: [],
          manualFragments: [],
          seenUuids: new Set<string>(),
        },
      );

    // Transform all catalog variant packs into CatalogIngredients (aka ItemListOption[])
    const catalogIngredients = adaptAndGroupVariantPacks(
      allIngredientFragmentsLists?.catalogVariantPacks ?? [],
    );

    // Transform all manual fragments into ListOption<ManualIngredientMetadata>[] (aka ManualingredientFragments)
    const manualIngredients =
      allIngredientFragmentsLists?.manualFragments.map((fragment) =>
        transformFragmentIntoManualIngredient(
          fragment,
          currencyCode || DEFAULT_CURRENCY_CODE,
        ),
      ) ?? [];

    // Combine catalog ingredients and manual ingredients into a single array
    return { catalog: catalogIngredients, manual: manualIngredients };
  }, [
    adaptAndGroupVariantPacks,
    allIngredientsResponse?.menu_item_ingredients,
    currencyCode,
  ]);

  const catalogIngredients = useMemo(
    () =>
      (searchCatalogIngredients.items || []).reduce<ItemListOption[]>(
        (acc, item) => {
          const { skus = [] } = item.metadata!;
          const allowedSkus = skus.filter(
            (sku) =>
              !sku.paidSearch &&
              ALLOWED_PACK_UNITS_FOR_CONVERSION.has(
                sku.baseUnit ?? sku.packUnit,
              ),
          );
          if (allowedSkus.length) {
            acc.push({
              ...item,
              metadata: {
                ...item.metadata!,
                skus: allowedSkus,
              },
            });
          }
          return acc;
        },
        [],
      ),
    [searchCatalogIngredients.items],
  );

  const searchIngredients = useCallback(
    async (query: string) => {
      try {
        await Promise.all([
          searchCatalogIngredients.searchItems(query),
          searchManualIngredients(query),
        ]);
      } catch (error) {
        showErrorToast();
      }
    },
    [searchCatalogIngredients, searchManualIngredients, showErrorToast],
  );

  return {
    allIngredients,
    catalogIngredients,
    clearSearch: () => {
      searchCatalogIngredients.clearSearch();
      setManualIngredientSearchResults(null);
    },
    manualIngredients: manualIngredientSearchResults,
    queryLoading: queryLoading ?? allIngredientsLoading,
    searchIngredients,
  };
};
